import { Calculate as QuantityCostIcon } from "@mui/icons-material";
import BoltIcon from "@mui/icons-material/Bolt";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Button,
  FormControlLabel,
  Popover,
  Radio,
  RadioGroup,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import React, { useState } from "react";
import ContentCard from "../../components/ContentCard";

const ROMDropdown = ({
  value,
  onChange,
  revenueQty,
  revenueUnitCost,
  costQty,
  costUnitCost,
  budgetQty,
  budgetUnitCost,
  type = "revenue", // 'revenue', 'cost', or 'budget'
  onQuantityChange,
  onUnitCostChange,
  rowIndex,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [tempSelection, setTempSelection] = useState(value || {});
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setTempSelection(value || {});
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    onChange(tempSelection);
    handleClose();
  };

  const handleCancel = () => {
    setTempSelection(value || {});
    handleClose();
  };

  const handleRevenueExpectedChange = (event, newValue) => {
    if (newValue !== null) {
      const isRevenueExpected = newValue === "yes";
      setTempSelection({
        ...tempSelection,
        revenueExpected: isRevenueExpected,
        method: isRevenueExpected ? tempSelection.method || "latest_cost" : null,
        manualValue: isRevenueExpected ? tempSelection.manualValue : null,
      });
    }
  };

  const handleMethodChange = (event) => {
    const method = event.target.value;
    setTempSelection({
      ...tempSelection,
      method: event.target.value,
      manualValue: event.target.value === "manual" ? tempSelection.manualValue : null,
    });

    if (method === "manual" || method === "latest_cost") {
      if (type === "revenue") {
        onQuantityChange(rowIndex, "revenueQty", 0);
        onUnitCostChange(rowIndex, "revenueUnitCost", 0);
      } else if (type === "cost") {
        onQuantityChange(rowIndex, "costQty", 0);
        onUnitCostChange(rowIndex, "costUnitCost", 0);
      } else if (type === "budget") {
        onQuantityChange(rowIndex, "budgetQty", 0);
        onUnitCostChange(rowIndex, "budgetUnitCost", 0);
      }
    }
  };

  const handleManualValueChange = (event) => {
    setTempSelection({
      ...tempSelection,
      manualValue: Number.parseFloat(event.target.value) || 0,
    });
  };

  const calculateQuantityCost = () => {
    if (type === "revenue") {
      return (revenueQty || 0) * (revenueUnitCost || 0);
    } else if (type === "cost") {
      return (costQty || 0) * (costUnitCost || 0);
    } else if (type === "budget") {
      return (budgetQty || 0) * (budgetUnitCost || 0);
    }
    return 0;
  };

  const getDisplayValue = () => {
    if (!value?.revenueExpected) {
      return "$0.00";
    }

    if (value.method === "latest_cost") {
      return "$0.00"; // fetched from your data
    } else if (value.method === "quantity_cost") {
      return `$${getCalculatedValue().toFixed(2)}`;
    } else if (value.method === "manual") {
      return `$${(value.manualValue || 0).toFixed(2)}`;
    }

    return "$0.00";
  };

  const getCalculatedValue = () => {
    if (!value?.revenueExpected) {
      return 0;
    }

    if (value.method === "latest_cost") {
      return 0; // fetched from your data
    } else if (value.method === "quantity_cost") {
      return calculateQuantityCost();
    } else if (value.method === "manual") {
      return value.manualValue || 0;
    }

    return 0;
  };

  const getQuantityValue = () => {
    if (type === "revenue") return revenueQty || 0;
    if (type === "cost") return costQty || 0;
    return budgetQty || 0;
  };

  const getUnitCostValue = () => {
    if (type === "revenue") return revenueUnitCost || 0;
    if (type === "cost") return costUnitCost || 0;
    return budgetUnitCost || 0;
  };

  return (
    <>
      <Box
        onClick={handleClick}
        display="flex"
        alignItems="center"
        gap={0.5}
        justifyContent="flex-end"
        sx={{
          cursor: "pointer",
          "&:hover": {
            opacity: 0.7,
          },
        }}
      >
        {type === "cost" ? <WarningAmberIcon fontSize="small" /> : <BoltIcon fontSize="small" />}
        <Typography variant="body2">{getDisplayValue()}</Typography>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCancel}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            minWidth: 340,
          },
        }}
      >
        <ContentCard elevation={0} p={3} sx={{ boxShadow: "none" }}>
          <ToggleButtonGroup
            value={tempSelection.revenueExpected ? "yes" : "no"}
            exclusive
            onChange={handleRevenueExpectedChange}
            sx={{
              mb: 3,
              width: "100%",
              "& .MuiToggleButton-root": {
                flex: 1,
                py: 0.75,
                px: 2,
                fontSize: "0.70rem",
                fontWeight: 500,
                border: "1px solid #e0e0e0",
                "&.Mui-selected": {
                  backgroundColor: "#1976d2",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "#1565c0",
                  },
                },
                "&:not(.Mui-selected)": {
                  backgroundColor: "#f5f5f5",
                  color: "#333",
                  "&:hover": {
                    backgroundColor: "#eeeeee",
                  },
                },
              },
            }}
          >
            <ToggleButton value="yes">Revenue Expected</ToggleButton>
            <ToggleButton value="no">None Expected</ToggleButton>
          </ToggleButtonGroup>

          {tempSelection.revenueExpected && (
            <>
              <RadioGroup
                value={tempSelection.method || "latest_cost"}
                onChange={handleMethodChange}
                sx={{ mb: 2 }}
              >
                <FormControlLabel
                  value="latest_cost"
                  control={<Radio size="small" sx={{ color: "#1976d2" }} />}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <BoltIcon sx={{ fontSize: "1rem", color: "#666" }} />
                      <Typography sx={{ fontSize: "0.875rem" }}>Latest Cost</Typography>
                    </Box>
                  }
                  sx={{ mb: 1 }}
                />
                <FormControlLabel
                  value="quantity_cost"
                  control={<Radio size="small" sx={{ color: "#1976d2" }} />}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <QuantityCostIcon sx={{ fontSize: "1rem", color: "#666" }} />
                      <Typography sx={{ fontSize: "0.875rem" }}>Quantity x Unit Cost</Typography>
                    </Box>
                  }
                  sx={{ mb: 1 }}
                />
                <FormControlLabel
                  value="manual"
                  control={<Radio size="small" sx={{ color: "#1976d2" }} />}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <WarningAmberIcon sx={{ fontSize: "1rem", color: "#666" }} />
                      <Typography sx={{ fontSize: "0.875rem" }}>Manual</Typography>
                    </Box>
                  }
                />
              </RadioGroup>
              {tempSelection.method === "manual" && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    type="number"
                    label="Manual Value"
                    value={tempSelection.manualValue || ""}
                    onChange={handleManualValueChange}
                    size="small"
                    fullWidth
                    inputProps={{ step: "0.01" }}
                    sx={{
                      "& .MuiInputBase-root": {
                        fontSize: "0.875rem",
                      },
                    }}
                  />
                </Box>
              )}

              {tempSelection.method === "quantity_cost" && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: "#f8f9fa",
                    borderRadius: 1,
                    border: "1px solid #e9ecef",
                  }}
                >
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, mb: 1 }}>
                    Calculated Value:
                  </Typography>
                  <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "#1976d2" }}>
                    ${getCalculatedValue().toFixed(2)}
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "#666", mt: 0.5 }}>
                    ({getQuantityValue()} × ${getUnitCostValue()})
                  </Typography>
                </Box>
              )}
            </>
          )}
          <Box sx={{ mt: 3, display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button size="small" onClick={handleCancel} sx={{ fontSize: "0.875rem" }}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleApply}
              sx={{ fontSize: "0.875rem" }}
            >
              Apply
            </Button>
          </Box>
        </ContentCard>
      </Popover>
    </>
  );
};

ROMDropdown.propTypes = {
  value: PropTypes.shape({
    revenueExpected: PropTypes.bool,
    method: PropTypes.string,
    manualValue: PropTypes.number,
  }),
  onChange: PropTypes.func.isRequired,
  revenueQty: PropTypes.number,
  revenueUnitCost: PropTypes.number,
  costQty: PropTypes.number,
  costUnitCost: PropTypes.number,
  budgetQty: PropTypes.number,
  budgetUnitCost: PropTypes.number,
  type: PropTypes.oneOf(["revenue", "cost", "budget"]),
  onQuantityChange: PropTypes.func.isRequired,
  onUnitCostChange: PropTypes.func.isRequired,
  rowIndex: PropTypes.number.isRequired,
};

export default ROMDropdown;
